import {
  AppBar,
  Box,
  Button,
  Checkbox,
  FormHelperText,
  Toolbar,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EditIcon from "@mui/icons-material/ModeEditRounded";
import DeleteIcon from "@mui/icons-material/DeleteRounded";
import { Wrapper } from "./styles";
import UserTable from "../../../Member/components/UserTable/UserTable";
import { ControlForm, Modal } from "../../../../components";
import AccountLayout from "../../../Member/components/AccountLayout";
import {
  ICreateDepartmentModel,
  IDepartment
} from "../../../../models/department";
import { TagInput } from "../../../WorkflowManagement/components/TagInput/TagInput";
import { Controller, useForm } from "react-hook-form";
import { ITokenPayload, IUser } from "../../../../models/user";
import CompanyService from "../../../../services/api/company.service";
import { toggleMessage } from "../../../../components/Toast/Toast";
import GroupRemoveRoundedIcon from "@mui/icons-material/GroupRemoveRounded";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";
import KeyboardBackspaceRoundedIcon from "@mui/icons-material/KeyboardBackspaceRounded";
import CircleIcon from "@mui/icons-material/Circle";
import authTokens from "../../../../services/local/auth-tokens";
import { toggleConfirmModal } from "../../../../components/ConfirmModal/ConfirmModal";
import {
  DepartMentPermissionModel,
  FeatureModel,
  TagType
} from "../../../../models/common/models.enum";
import { useApi } from "../../../../hooks/useApi.hook";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import InputForm from "../../../../components/controls/InputForm";
import RoleService from "../../../../services/api/role.service";
import { IFeaturePermission } from "../../../../models/user/models.roles";
import { theme } from "../../../../theme/theme";
import { ICompany, ICreateCompanyReq } from "../../../../models/company";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const DetailDepartment = () => {
  const today = dayjs();
  const { t, i18n } = useTranslation();

  const params = useParams();
  const [canDelete, setCanDelete] = useState<boolean>(false);
  const [openCreate, setOpenCreate] = useState<boolean>(false);
  const [checked, setChecked] = useState<boolean>(false);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<ITokenPayload | null>(null);
  const getSingle = useApi<ICompany>({ isFetch: true });
  const updateCompany = useApi<ICreateCompanyReq>({});
  const deleteDepartment = useApi<ICompany>({});
  const [item, setItem] = useState<ICompany | undefined>(undefined);
  const [userId, setUserId] = useState<string | undefined>();
  const [permissions, setPermissions] = useState<string[]>([]);
  const getFeaturePersmission = useApi<IFeaturePermission[]>({ isFetch: true });
  const matches = useMediaQuery(theme.breakpoints.down("md"));
  const [locale, setLocale] = useState<string>();

  useEffect(() => {
    setLocale(i18n.language);
    dayjs.locale(locale);
  }, [i18n.language]);

  function fetchData() {
    if (params.id) {
      getSingle.request(CompanyService.getSingle(params.id));
    }
    setCanEdit(false);
  }

  useEffect(() => {
    if (userId)
      getFeaturePersmission.request(
        RoleService.getFeaturePemissionByUserId(userId)
      );
  }, [userId]);

  useEffect(() => {
    async function getUserInfo() {
      const user = await authTokens.getUser();
      user && setUserId(user.userId);
    }
    getUserInfo();
    fetchData();
  }, []);

  useEffect(() => {
    if (getFeaturePersmission.data) {
      setPermissions(
        getFeaturePersmission.data.find(
          f => f.feature === FeatureModel.Department
        )?.permissions ?? []
      );
    }
  }, [getFeaturePersmission.data]);

  useEffect(() => {
    setItem(getSingle.data);
    setValue("companyName", getSingle.data?.companyName);
    setValue("connectionString", getSingle.data?.connectionString);
    setValue("userName", getSingle.data?.userName);
    setValue("email", getSingle.data?.email);
    setValue("phoneNumber", getSingle.data?.phoneNumber);
    setValue(
      "taxIdentificationNumber",
      getSingle.data?.taxIdentificationNumber
    );
    setValue("address", getSingle.data?.address);
    setValue("numberOfUsersCreated", getSingle.data?.numberOfUsersCreated);

    setValue(
      "expirationDate",
      getSingle.data?.expirationDate
        ? dayjs(getSingle.data?.expirationDate)
        : null
    );
    // setValue("leaderId", getSingle.data?.leaderId);
  }, [getSingle.data]);

  const validationSchema = Yup.object().shape({
    companyName: Yup.string().required(t("Department.PleaseEnterCompanyName")),
    connectionString: Yup.string().required(
      t("Department.PleaseEnterConnectionString")
    ),
    userName: Yup.string().required(t("Department.PleaseEnterUsername")),
    phoneNumber: Yup.string().required(t("Department.PleaseEnterPhoneNumber")),
    taxIdentificationNumber: Yup.string().required(
      t("Department.PleaseEnterTaxIdentificationNumber")
    ),
    address: Yup.string().required(t("Department.PleaseEnterAddress")),
    numberOfUsersCreated: Yup.string().required(
      t("Department.PleaseEnterNumberOfUsersCreated")
    ),
    email: Yup.string()
      .email()
      .matches(
        /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$/,
        t("Department.InvalidEmailFormat")
      )
      .required(t("Department.PleaseEnterEmail"))
  }) as any;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<any>({
    defaultValues: useMemo(() => item, [item]),
    resolver: yupResolver(validationSchema)
  });

  useEffect(() => {
    if (updateCompany.success) {
      toggleMessage({
        type: "success",
        message: t("UserDetail.ToastUpdateSuccess")
      });
      setOpenCreate(false);
      setCanEdit(false);
      setChecked(false);
      reset();
      fetchData();
      if (canDelete) {
        setCanDelete(false);
      }
    }
    if (updateCompany.error) {
      toggleMessage({
        type: "error",
        message: updateCompany?.error ?? t("UserDetail.ToastErrorDefault")
      });
    }
  }, [updateCompany.success, updateCompany.error]);

  useEffect(() => {
    if (deleteDepartment.success) {
      toggleMessage({
        type: "success",
        message: t("Department.DeleteCompanySuccessMessage")
      });
      setOpenCreate(false);
      navigate(-1);
    }
    if (deleteDepartment.error) {
      toggleMessage({
        type: "error",
        message: updateCompany?.error ?? t("UserDetail.ToastErrorDefault")
      });
    }
  }, [deleteDepartment.success, deleteDepartment.error]);

  const onSubmit = (request: ICreateCompanyReq) => {
    if (item) {
      const old = item?.infoMembers?.map(member => member.id);

      let memberIds: string[] = [];

      if (old) {
        const news = !canEdit
          ? request.memberIds?.map(member => member.id)
          : [];
        memberIds = openCreate ? [...old, ...news] : item.memberIds;
      }

      const companyName = canEdit ? request.companyName : item.companyName;
      const connectionString = canEdit
        ? request.connectionString
        : item.connectionString;

      const req: ICompany = {
        id: item.id,
        companyName: companyName,
        connectionString: connectionString,
        address: request.address,
        email: request.email,
        phoneNumber: request.phoneNumber,
        taxIdentificationNumber: request.taxIdentificationNumber,
        userName: request.userName,
        avatar: request.avatar,
        memberIds: memberIds,
        numberOfUsersCreated: request.numberOfUsersCreated,
        expirationDate: request.expirationDate,
        iframeGoogleAds: request.iframeGoogleAds,
        iframeGoogleReport: request.iframeGoogleReport,
        infoMembers: [],
        infoCEO: null
      };
      updateCompany.request(CompanyService.update(req));
    }
  };

  const onDeleteGroup = () => {
    toggleConfirmModal({
      title: t("Department.ConfirmDeleteCompanyTitle"),
      content: t("Department.ConfirmDeleteCompanyContent"),
      onSubmit: async () => {
        if (item) {
          item.id &&
            deleteDepartment.request(CompanyService.deleteGroup(item.id));
        }
      },
      submitText: t("UserDetail.ConfirmDeactivateSubmitText"),
      type: "warning-red"
    });
  };

  useEffect(() => {
    async function getUserInfo() {
      const user = await authTokens.getUser();
      user && setUserInfo(user);
    }
    getUserInfo();
  }, []);

  const handleDelete = (list: IUser[]) => {
    if (item) {
      const items = list.map(item => item.id);
      const req: ICompany = {
        id: item.id,
        companyName: item.companyName,
        connectionString: item.connectionString,
        address: item.address,
        email: item.email,
        phoneNumber: item.phoneNumber,
        taxIdentificationNumber: item.taxIdentificationNumber,
        userName: item.userName,
        avatar: item.avatar,
        infoMembers: [],
        memberIds: items,
        infoCEO: item.infoCEO,
        numberOfUsersCreated: item.numberOfUsersCreated,
        expirationDate: item.expirationDate,
        iframeGoogleAds: item.iframeGoogleAds,
        iframeGoogleReport: item.iframeGoogleReport
      };
      updateCompany.request(CompanyService.update(req));
    }
  };

  const getDisable = (): IUser[] | undefined => {
    if (item?.infoMembers) {
      return [...item?.infoMembers];
    } else {
      return undefined;
    }
  };

  return (
    <Wrapper>
      <AppBar position={matches ? undefined : "static"} color="default">
        <Toolbar>
          <Box className="header-page">
            <Button onClick={() => navigate(-1)}>
              <KeyboardBackspaceRoundedIcon />
            </Button>
            <Box>
              <Typography className="name-text" variant="subtitle1">
                {item?.companyName}
              </Typography>
              <Typography variant="body2">
                {item?.connectionString}
                <span>
                  <CircleIcon
                    sx={{
                      width: "4px",
                      height: "4px",
                      margin: "2px 6px",
                      color: "GrayText"
                    }}
                  />
                </span>
                <span>
                  {item?.infoMembers?.length} {t("Department.MembersSuffix")}
                </span>
              </Typography>
            </Box>
          </Box>
          <Box className="admin-action">
            {permissions.includes(DepartMentPermissionModel.Update) && (
              <Button
                variant={matches ? "text" : "outlined"}
                color="info"
                size="small"
                startIcon={<EditIcon />}
                disabled={canDelete}
                onClick={() => {
                  setCanEdit(true);
                  setOpenCreate(true);
                }}
              >
                {!matches && (
                  <Typography variant="subtitle2">
                    {t("Department.EditCompanyInfoModalTitle")}
                  </Typography>
                )}
              </Button>
            )}
            {permissions.includes(DepartMentPermissionModel.Update) && (
              <Button
                variant={matches ? "text" : "outlined"}
                color="primary"
                size="small"
                startIcon={<GroupAddRoundedIcon />}
                disabled={canDelete}
                onClick={() => setOpenCreate(true)}
              >
                {!matches && (
                  <Typography variant="subtitle2">
                    {t("Department.AddMembersPlaceholder")}
                  </Typography>
                )}
              </Button>
            )}
            {!!item?.infoMembers?.length &&
              permissions.includes(DepartMentPermissionModel.Update) && (
                <Button
                  variant={matches ? "text" : "outlined"}
                  color="error"
                  size="small"
                  startIcon={<GroupRemoveRoundedIcon />}
                  disabled={canDelete}
                  onClick={() => setCanDelete(!canDelete)}
                >
                  {!matches && (
                    <Typography variant="subtitle2">
                      {t("Department.DeleteMembersButton")}
                    </Typography>
                  )}
                </Button>
              )}
            {permissions.includes(DepartMentPermissionModel.Delete) && (
              <Button
                variant={matches ? "text" : "contained"}
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                disabled={canDelete}
                onClick={onDeleteGroup}
              >
                {!matches && (
                  <Typography variant="subtitle2">
                    {t("Department.DeleteCompanyButton")}
                  </Typography>
                )}
              </Button>
            )}
            <Modal
              title={
                canEdit
                  ? t("Department.EditCompanyInfoModalTitle")
                  : t("Department.AddMembersModalTitle")
              }
              textSubmit={t("UserDetail.ModalSubmitText")}
              textClose={t("UserDetail.ModalCancelText")}
              open={openCreate}
              onSubmit={handleSubmit(onSubmit)}
              onClose={() => {
                setCanEdit(false);
                setOpenCreate(false);
              }}
            >
              <form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px"
                }}
              >
                {canEdit && (
                  <Box>
                    <ControlForm
                      title={t("Department.CompanyNameLabel")}
                      classname="brand-form"
                      isRequired
                    >
                      <InputForm
                        placeholder={t("Department.CompanyNamePlaceholder")}
                        required
                        name={"companyName"}
                        errors={errors}
                        control={control}
                        size="small"
                      />
                    </ControlForm>
                    <ControlForm
                      title={t("UserDetail.UsernameLabel")}
                      classname="brand-form"
                      isRequired
                    >
                      <InputForm
                        placeholder={t("UserDetail.UsernamePlaceholder")}
                        required
                        name={"userName"}
                        errors={errors}
                        control={control}
                        size="small"
                      />
                    </ControlForm>
                    <ControlForm
                      title={t("UserDetail.EmailLabel")}
                      classname="brand-form"
                      isRequired
                    >
                      <InputForm
                        placeholder={t("UserDetail.EmailLabel")}
                        required
                        name={"email"}
                        errors={errors}
                        control={control}
                        size="small"
                      />
                    </ControlForm>
                    <ControlForm
                      title={t("UserDetail.PhoneNumberLabel")}
                      classname="brand-form"
                      isRequired
                    >
                      <InputForm
                        placeholder={t("UserDetail.PhoneNumberPlaceholder")}
                        required
                        name={"phoneNumber"}
                        errors={errors}
                        control={control}
                        size="small"
                      />
                    </ControlForm>
                    <ControlForm
                      title={t("Department.TaxIdentificationNumberLabel")}
                      classname="brand-form"
                      isRequired
                    >
                      <InputForm
                        placeholder={t(
                          "Department.TaxIdentificationNumberPlaceholder"
                        )}
                        required
                        name={"taxIdentificationNumber"}
                        errors={errors}
                        control={control}
                        size="small"
                      />
                    </ControlForm>
                    <ControlForm
                      title={t("UserDetail.AddressLabel")}
                      classname="brand-form"
                      isRequired
                    >
                      <InputForm
                        placeholder={t("UserDetail.AddressPlaceholder")}
                        required
                        name={"address"}
                        errors={errors}
                        control={control}
                        size="small"
                      />
                    </ControlForm>
                    <ControlForm
                      title={t("Department.NumberOfUsersCreatedLabel")}
                      classname="brand-form"
                      isRequired
                    >
                      <InputForm
                        placeholder={t(
                          "Department.NumberOfUsersCreatedPlaceholder"
                        )}
                        required
                        name={"numberOfUsersCreated"}
                        errors={errors}
                        control={control}
                        size="small"
                        type="number"
                      />
                    </ControlForm>
                    <ControlForm
                      title={t("Department.ExpirationDateLabel")}
                      classname="brand-form"
                    >
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale={locale}
                      >
                        <Controller
                          name="expirationDate"
                          control={control}
                          render={({
                            field: { onChange, value },
                            fieldState: { error, invalid }
                          }) => (
                            <>
                              <MobileDatePicker
                                disablePast
                                minDate={today}
                                value={value}
                                dayOfWeekFormatter={date => date.format("dd")}
                                onChange={newDate =>
                                  onChange(
                                    newDate
                                      ? dayjs(newDate)
                                          .hour(value ? value.hour() : 0)
                                          .minute(value ? value.minute() : 0)
                                      : null
                                  )
                                }
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    placeholder: t(
                                      "Department.ExpirationDatePlaceholder"
                                    ),
                                    InputProps: {
                                      readOnly: true,
                                      sx: {
                                        cursor: "pointer",
                                        "& .MuiSvgIcon-root": {
                                          width: 24,
                                          height: 24
                                        }
                                      }
                                    },
                                    sx: {
                                      "& input": {
                                        padding: "10px 12px!important"
                                      }
                                    }
                                  },
                                  toolbar: {
                                    className: "dialog-toolbar",
                                    sx: {
                                      span: {
                                        fontSize: 12
                                      },
                                      h4: {
                                        fontSize: 20
                                      }
                                    }
                                  },
                                  calendarHeader: {
                                    sx: {
                                      "& .MuiPickersCalendarHeader-labelContainer":
                                        {
                                          fontSize: 16
                                        },
                                      "& .MuiSvgIcon-root": {
                                        width: 24,
                                        height: 24
                                      },
                                      button: { fontSize: 18 }
                                    }
                                  },
                                  mobilePaper: {
                                    sx: {
                                      "& .MuiDayCalendar-weekDayLabel": {
                                        fontSize: 12
                                      },
                                      "& .MuiPickersDay-root": {
                                        fontSize: 12
                                      },
                                      "& .MuiPickersMonth-monthButton": {
                                        fontSize: 14
                                      },
                                      "& .MuiPickersYear-yearButton": {
                                        fontSize: 14
                                      },
                                      "& .MuiButtonBase-root": {
                                        fontSize: 14
                                      }
                                    }
                                  }
                                }}
                              />

                              {error && (
                                <FormHelperText sx={{ color: "#ED3E47" }}>
                                  {error.message}
                                </FormHelperText>
                              )}
                            </>
                          )}
                        />
                      </LocalizationProvider>
                    </ControlForm>

                    <ControlForm
                      title={t("Department.ConnectionStringLabel")}
                      isRequired
                      classname="brand-form"
                    >
                      <InputForm
                        placeholder={t(
                          "Department.ConnectionStringPlaceholder"
                        )}
                        required
                        name={"connectionString"}
                        errors={errors}
                        control={control}
                        size="small"
                        disabled={true}
                      />
                    </ControlForm>
                  </Box>
                )}
                {openCreate && !canEdit && (
                  <ControlForm
                    title={t("Department.MembersLabel")}
                    infor={t("Department.MembersNoteCanBeAddedLater")}
                    classname="brand-form"
                  >
                    <TagInput
                      store="company"
                      name={"memberIds"}
                      control={control}
                      errors={errors}
                      isError={!!errors.memberIds}
                      placeholder={t("Department.AddMembersPlaceholder")}
                      optionDisabled={getDisable()}
                      type={TagType.User}
                    />
                  </ControlForm>
                )}
              </form>
            </Modal>
          </Box>
        </Toolbar>
      </AppBar>

      <Box className="admin-container">
        <Box>
          <Typography className="name-text" variant="overline">
            {t("Department.AdminLabelUppercase")}
          </Typography>
          {item?.infoCEO ? (
            <Box marginTop="8px">
              <AccountLayout
                name={item?.infoCEO?.firstName ?? ""}
                tag={item?.infoCEO!?.userName ?? ""}
                position={item?.infoCEO!?.position ?? ""}
                avatar={item?.infoCEO?.avatar}
              />
            </Box>
          ) : (
            <Typography variant="body2" marginTop="8px">
              {t("Department.NoAdminYet")}
            </Typography>
          )}
        </Box>
      </Box>

      <Box className="main-container">
        {item && (
          <UserTable
            data={item?.infoMembers}
            canEdit={canDelete}
            cancel={() => setCanDelete(!canDelete)}
            deleted={handleDelete}
          />
        )}
      </Box>
    </Wrapper>
  );
};

export default DetailDepartment;
